import { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = "atr_navbar_cache_v1";

// Module-level cache — shared across ALL <Navbar/> instances on the same tab.
// Prevents the "flash of default navbar" when navigating between routes
// because each page component mounts its own <Navbar/>.
let _cache = null;
let _promise = null;
const _subscribers = new Set();

// Seed from sessionStorage synchronously so the FIRST render already has
// the user's saved config (no flash across page reloads either).
try {
  const raw = sessionStorage.getItem(CACHE_KEY);
  if (raw) _cache = JSON.parse(raw);
} catch (_) {
  // ignore parse / access errors
}

const _persist = (data) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch (_) {
    // ignore quota / access errors
  }
};

const _fetchNavbar = () => {
  if (_promise) return _promise;
  _promise = axios
    .get(`${API}/content/navbar`)
    .then((r) => {
      const items = Array.isArray(r.data?.items) ? r.data.items : [];
      const data = {
        items,
        book_button_label: r.data?.book_button_label || "RDV",
      };
      _cache = data;
      _persist(data);
      _subscribers.forEach((cb) => cb(data));
      _promise = null;
      return data;
    })
    .catch((err) => {
      _promise = null;
      throw err;
    });
  return _promise;
};

/**
 * Returns the navbar config (items + book_button_label).
 * - First render: returns cache (sessionStorage or previously-fetched) if any,
 *   else the passed-in `fallback`.
 * - Kicks off a background fetch if no fresh cache; all consumers receive the
 *   update via subscription so all <Navbar/> instances update in sync.
 */
export const useNavbarConfig = (fallback) => {
  const [config, setConfig] = useState(
    () => _cache || { items: fallback || [], book_button_label: "RDV" }
  );

  useEffect(() => {
    const cb = (data) => setConfig(data);
    _subscribers.add(cb);
    if (!_cache) {
      _fetchNavbar().catch(() => {});
    }
    return () => {
      _subscribers.delete(cb);
    };
  }, []);

  return config;
};

/**
 * Force a re-fetch — call after admin saves the navbar so all open tabs
 * (or, more commonly, the admin's own preview tab) refresh immediately.
 */
export const invalidateNavbarCache = () => {
  _cache = null;
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch (_) {
    // ignore
  }
  _fetchNavbar().catch(() => {});
};
